// Featured Events upgrade — member-facing event photo carousel.
//
// The primary flyer is always the FIRST image, followed by admin-ordered
// additional photos. Behaviour mirrors the written spec:
//   • 0 images  → render nothing (graceful fallback preserved).
//   • 1 image   → single image, tap to enlarge (same as the old EventFlyer).
//   • 2+ images → carousel: one large image, left/right arrows, counter,
//                 thumbnail strip, touch-swipe, tap-to-enlarge lightbox.
//
// The gallery is detail-page ONLY — it is never used on the Home page.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CaretLeft, CaretRight } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogTitle } from '../ui/dialog';
import { eventMediaAPI } from '../../lib/eventMediaApi';

export default function EventPhotoGallery({ flyerUrl, eventName, kind, eventId }) {
  const [photos, setPhotos] = useState([]);
  const [idx, setIdx] = useState(0);
  const [zoom, setZoom] = useState(false);
  const touchStartX = useRef(null);

  useEffect(() => {
    let alive = true;
    if (!kind || !eventId) { setPhotos([]); return undefined; }
    eventMediaAPI.listPhotos(kind, eventId)
      .then((r) => { if (alive) setPhotos(r.data?.photos || []); })
      .catch(() => { if (alive) setPhotos([]); });
    return () => { alive = false; };
  }, [kind, eventId]);

  // Build the ordered slide list: flyer first, then gallery photos.
  const slides = useMemo(() => {
    const out = [];
    if (flyerUrl) out.push({ id: 'flyer', url: flyerUrl });
    for (const p of photos) if (p.url) out.push({ id: p.id, url: p.url });
    return out;
  }, [flyerUrl, photos]);

  const total = slides.length;
  useEffect(() => { if (idx >= total) setIdx(0); }, [total, idx]);

  if (total === 0) return null;

  const go = (n) => setIdx(((n % total) + total) % total);
  const current = slides[Math.min(idx, total - 1)];

  const onTouchStart = (e) => { touchStartX.current = e.touches[0]?.clientX ?? null; };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null || total < 2) return;
    const dx = (e.changedTouches[0]?.clientX ?? 0) - touchStartX.current;
    if (Math.abs(dx) > 40) { if (dx < 0) go(idx + 1); else go(idx - 1); }
    touchStartX.current = null;
  };

  const multi = total > 1;

  return (
    <div data-testid="event-photo-gallery" data-photo-count={total}>
      <div
        className="relative w-full overflow-hidden rounded-2xl border border-[#e3dcd1] bg-[#f7f4ed]"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        <button
          type="button"
          onClick={() => setZoom(true)}
          data-testid="event-gallery-main"
          className="block w-full focus:outline-none focus:ring-2 focus:ring-[#015e3f]/30"
          aria-label={`Enlarge ${eventName || 'event'} photo ${idx + 1} of ${total}`}
        >
          <img
            key={current.id}
            src={current.url}
            alt={eventName ? `${eventName} photo ${idx + 1}` : 'Event photo'}
            className="block w-full h-auto max-h-[70vh] object-contain mx-auto"
            data-testid="event-gallery-img"
            loading={idx === 0 ? 'eager' : 'lazy'}
            decoding="async"
          />
        </button>

        {multi && (
          <>
            <button
              type="button"
              aria-label="Previous photo"
              onClick={(e) => { e.stopPropagation(); go(idx - 1); }}
              data-testid="event-gallery-prev"
              className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/40 hover:bg-black/60 text-white border border-white/20 backdrop-blur-sm flex items-center justify-center transition-colors"
            >
              <CaretLeft size={16} weight="bold" />
            </button>
            <button
              type="button"
              aria-label="Next photo"
              onClick={(e) => { e.stopPropagation(); go(idx + 1); }}
              data-testid="event-gallery-next"
              className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/40 hover:bg-black/60 text-white border border-white/20 backdrop-blur-sm flex items-center justify-center transition-colors"
            >
              <CaretRight size={16} weight="bold" />
            </button>
            <div
              data-testid="event-gallery-counter"
              className="absolute bottom-2 right-2 text-[11px] font-medium text-white bg-black/50 rounded-full px-2 py-0.5 backdrop-blur-sm"
            >
              {idx + 1} / {total}
            </div>
          </>
        )}
      </div>

      {/* Thumbnail strip — only when there is more than one image. */}
      {multi && (
        <div
          data-testid="event-gallery-thumbs"
          className="mt-2 flex gap-2 overflow-x-auto pb-1 -mx-1 px-1"
        >
          {slides.map((s, i) => (
            <button
              key={s.id}
              type="button"
              onClick={() => go(i)}
              data-testid={`event-gallery-thumb-${i}`}
              aria-label={`Show photo ${i + 1}`}
              aria-current={i === idx}
              className={`shrink-0 w-14 h-14 rounded-lg overflow-hidden border-2 transition-colors ${
                i === idx ? 'border-[#015e3f]' : 'border-transparent opacity-70 hover:opacity-100'
              }`}
            >
              <img
                src={s.url}
                alt=""
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover"
              />
            </button>
          ))}
        </div>
      )}

      <Dialog open={zoom} onOpenChange={setZoom}>
        <DialogContent data-testid="event-gallery-zoom-dialog" className="sm:max-w-3xl bg-black/95 p-0 border-none">
          <DialogTitle className="sr-only">{eventName || 'Event photo'}</DialogTitle>
          <div className="flex items-center justify-center p-4 sm:p-6">
            <img
              src={current.url}
              alt={eventName ? `${eventName} photo` : 'Event photo'}
              className="block max-w-full max-h-[80vh] object-contain"
              data-testid="event-gallery-zoom-img"
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
